import type { JSX } from 'react';
import { Translation, TranslationWithArgs1 } from '../../common/i18n/Text.ts';
import * as Date from 'dg-web-shared/lib/Date.ts';
import {
    ColumnWidth,
    DefaultTableRow,
    EmptyTableHeaderColumn,
    SortDirection,
    Table,
    TableBody,
    TableColumn,
    TableHeader,
    TableHeaderColumn,
} from '../../ui/table/Table.tsx';
import { tomSyncStatusTexts, tomTexts } from '../i18n/TomTexts.ts';
import * as TomLayoutState from '../state/TomLayoutState.ts';
import * as FirmwareState from '../../firmware/state/SimpleTomFirmwareState.ts';
import * as FirmwareUtils from '../../firmware/FirmwareUtil.ts';
import * as TomState from '../state/TomState.ts';
import { Store, useStore } from 'dg-web-shared/lib/Flux.tsx';
import { Body } from '../../ui/layout/TabContent.tsx';
import { AbstractLegacyServerState } from 'dg-web-shared/lib/AbstractLegacyServerStateSlice.ts';
import { TomSyncStatus } from './TomSyncStatus.tsx';
import * as Tom from 'dg-web-shared/model/Tom.ts';
import { ParsedTom, TomStatus } from 'dg-web-shared/model/Tom.ts';
import { TomDetailSlideIn } from './TomDetailSlideIn.tsx';
import { OutOfServiceHalfSlideIn } from './out-of-service/OutOfServiceHalfSlideIn.tsx';
import { OutOfServiceDateHalfSlideIn } from './out-of-service/OutOfServiceDateHalfSlideIn.tsx';
import { SyncAppSlideIn } from '../../sync-app/components/SyncAppSlideIn.tsx';
import * as SyncAppHelpSlideInState from '../../sync-app/state/SyncAppHelpSlideInState.ts';
import { Conditional } from 'dg-web-shared/lib/ReactHelpers.tsx';
import {
    TomFirmwareStatus,
    TomStatusIconPositioning,
} from './TomFirmwareStatus.tsx';
import {
    FilterLayout,
    FilterLayoutBody,
    FilterLayoutHeader,
} from '../../ui/filter/FilterLayout.tsx';
import {
    ButtonDropdown,
    ButtonDropdownItem,
    ButtonDropdownLinkItem,
} from '../../ui/slidein/ButtonDropdown.tsx';
import { RestrictedComponent } from '../../app/components/RestrictedComponent.tsx';
import { CustomPermissionCheckers } from '../../app/state/Permission.ts';
import { TomOperationStatusLabel } from './TomError.tsx';
import * as NumberFormatter from 'dg-web-shared/lib/NumberFormatter.ts';
import { IconTopOffsetContainer } from '../../zones/components/ZoneListBody.tsx';
import { OperatorAppRoutes } from '../../app/config/OperatorRoutingConfig.tsx';
import { Icon16 } from '../../ui/icons/Icon.tsx';
import { chevronRight } from 'dg-web-shared/ui/icons/Icons16.tsx';
import { OperatorContext } from '../../app/components/BaseLayoutAndData.tsx';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { useContext } from 'react';
import { urlWithOperatorExternalId } from '../../utils/Utils.ts';

export interface TomTexts {
    TabTitle: Translation;
    InternalId: Translation;
    OperatorExternalId: Translation;
    Tom: Translation;
    Name: Translation;
    PaymentType: Translation;
    CashboxType: Translation;
    Address: Translation;
    Place: Translation;
    ZipCode: Translation;
    Location: Translation;
    State: Translation;
    StateMessages: Translation;
    LastContact: Translation;
    LastCashBoxLevel: Translation;
    LastBatteryVoltage: Translation;
    LastVoltage: Translation;

    InOperationSince: Translation;
    LastCollection: Translation;
    LastCollectionNever: Translation;

    CreationDate: Translation;
    FirmwareVersion: Translation;
    SyncStatus: Translation;
    SlideInHeaderCaption: Translation;

    Zone: Translation;
    Permissions: Translation;
    AcceptedCoins: Translation;
    AcceptedTokens: Translation;
    PaymentTransactionPeriod: Translation;
    Spaces: Translation;

    PayByPlateTimelimitNote: Translation;
    PayBySpaceTimelimitNote: Translation;

    Actions: Translation;
    CSVExport: Translation;
    WriteToStick: Translation;
    SaveEdit: Translation;
    FirmwareStatus: Translation;
    UpdateStick: Translation;
    PaymentTypeOptions: TranslationWithArgs1<Tom.TomPaymentType>;
    CashboxTypeOptions: TranslationWithArgs1<Tom.TomModelType>;
}

interface ItemProps {
    allState: Store;
    tom: Tom.ParsedTom;
    language: string;
    showOperatorExternalId: boolean;
    showPaymentType: boolean;
    showCashboxType: boolean;
    showSyncState: boolean;
}

const openUsbApp = function (
    operatorId: number,
    tomIds: number[],
    firmware: string,
) {
    let uri = 'parkingportal-sync://?action=multiTomUpdate';
    uri += '&operatorId=' + encodeURIComponent(operatorId.toString());
    uri += '&tomIds=' + encodeURIComponent(tomIds.join(','));
    uri += '&firmwareVersion=' + encodeURIComponent(firmware);
    window.location.assign(uri);
};

const TomItem = (p: ItemProps): JSX.Element => {
    const txt = tomTexts[p.language];
    return (
        <DefaultTableRow
            onClick={() =>
                p.allState.update(store =>
                    TomLayoutState.Layout.stateWrite(store, {
                        selectedTomId: p.tom.tomId,
                    }),
                )
            }
        >
            <TableColumn width={ColumnWidth._24px}>
                <IconTopOffsetContainer>
                    <TomOperationStatusLabel
                        tom={p.tom}
                        language={p.language}
                    />
                </IconTopOffsetContainer>
            </TableColumn>
            <TableColumn width={ColumnWidth._72px}>{p.tom.tomId}</TableColumn>
            <Conditional c={p.showOperatorExternalId}>
                <TableColumn width={ColumnWidth._72px}>
                    {p.tom.operatorExternalId}
                </TableColumn>
            </Conditional>
            <TableColumn width={ColumnWidth.variable}>{p.tom.name}</TableColumn>
            <Conditional c={p.showPaymentType}>
                <TableColumn width={ColumnWidth._136px}>
                    {txt.PaymentTypeOptions(p.tom.paymentType)}
                </TableColumn>
            </Conditional>
            <Conditional c={p.showCashboxType}>
                <TableColumn width={ColumnWidth._136px}>
                    {txt.CashboxTypeOptions(p.tom.model)}
                </TableColumn>
            </Conditional>
            <TableColumn width={ColumnWidth._136px}>
                {p.tom.lastCashBoxLevel
                    ? NumberFormatter.numberToPrice(
                          p.tom.lastCashBoxLevel / 100,
                          true,
                      )
                    : ' - '}
            </TableColumn>
            <TableColumn width={ColumnWidth._136px}>
                {p.tom.lastCollection
                    ? Date.Formatter.durationFromNow(
                          p.tom.lastCollection,
                          p.language,
                      )
                    : txt.LastCollectionNever()}
            </TableColumn>
            <TableColumn width={ColumnWidth._108px}>
                {p.tom.lastBatteryVoltage
                    ? (p.tom.lastBatteryVoltage / 1000).toFixed(3) + ' V'
                    : '-'}
            </TableColumn>
            <TableColumn width={ColumnWidth._136px}>
                <TomFirmwareStatus tom={p.tom} language={p.language} />
            </TableColumn>
            <Conditional c={p.showSyncState}>
                <TableColumn width={ColumnWidth._136px}>
                    <TomStatusIconPositioning
                        label={
                            <TomSyncStatus
                                status={p.tom.synchronizationStatus}
                            />
                        }
                        text={tomSyncStatusTexts[
                            p.language
                        ].SynchronizationStatus(p.tom.synchronizationStatus)}
                    />
                </TableColumn>
            </Conditional>
            <TableColumn width={ColumnWidth._24px} captionFontSize>
                <IconTopOffsetContainer>
                    <Icon16 icon={chevronRight} />
                </IconTopOffsetContainer>
            </TableColumn>
        </DefaultTableRow>
    );
};

export function TomsBody() {
    const { operatorExternalId } = useContext(OperatorContext);
    const { storeState, store } = useStore(store => ({
        layout: TomLayoutState.Layout.get(store),
        toms: TomState.List.get(store),
        firmwares: FirmwareState.List.get(store),
        syncSlideIn: SyncAppHelpSlideInState.Layout.get(store),
    }));
    const { language } = useLanguage();

    function getDependingStateSlices(): AbstractLegacyServerState<
        ParsedTom[]
    >[] {
        return [storeState.toms];
    }

    function renderSlideIns(): JSX.Element[] {
        return [
            <TomDetailSlideIn key="tom-detail" />,
            <SyncAppSlideIn key="sync-app" />,
            <OutOfServiceHalfSlideIn key="tom-plan-out-of-service" />,
            <OutOfServiceDateHalfSlideIn key="out-of-service-dates" />,
        ];
    }

    function isBodyDisabled(): boolean {
        return (
            storeState.syncSlideIn.open || storeState.layout.outOfServiceOpen
        );
    }

    const txt = tomTexts[language];
    const tomsToDisplay = storeState.toms.data.filter(
        t => t.state === TomStatus.OPERATIONAL,
    );
    const firmwares = storeState.firmwares.data;
    const tomsWithOldFirmware = tomsToDisplay.filter(
        t => t.firmwareId !== t.targetFirmwareId,
    );

    const idsOfDisplayedToms = tomsToDisplay.map(tom => tom.tomId);
    // show operator external id in table if at least one tom has it set
    const showOperatorExternalId = !!tomsToDisplay.find(
        t => !!t.operatorExternalId,
    );

    const showPaymentType = !tomsToDisplay
        .map(t => t.paymentType)
        .every((val, _i, arr) => val === arr[0]);
    const showCashboxType = !tomsToDisplay
        .map(t => t.model)
        .every((val, _i, arr) => val === arr[0]);
    const showSyncState = !!tomsToDisplay.find(t => !t.lastOnlineStatus);
    return (
        <Body
            disabled={isBodyDisabled()}
            dependingStateSlices={getDependingStateSlices()}
            slideIns={renderSlideIns()}
        >
            <FilterLayout>
                <FilterLayoutHeader
                    filterSummary={<div />}
                    rightDropdown={
                        <ButtonDropdown label={txt.Actions()}>
                            <RestrictedComponent
                                route={OperatorAppRoutes.ParkingMeters}
                                permissionChecker={
                                    CustomPermissionCheckers.syncAppInteractionAllowed
                                }
                            >
                                <div>
                                    <ButtonDropdownItem
                                        disabled={
                                            tomsWithOldFirmware.length === 0
                                        }
                                        onClick={() =>
                                            openUsbApp(
                                                tomsToDisplay[0].operatorId,
                                                idsOfDisplayedToms,
                                                FirmwareUtils.getVersionString(
                                                    firmwares.find(
                                                        t =>
                                                            t.firmwareId ===
                                                            tomsWithOldFirmware[0]
                                                                .targetFirmwareId,
                                                    )!,
                                                ),
                                            )
                                        }
                                        label={txt.UpdateStick()}
                                    />
                                    <ButtonDropdownLinkItem
                                        label={txt.CSVExport()}
                                        href={urlWithOperatorExternalId(
                                            '/ui-api/operator-account/taxomex/tom/csv',
                                            operatorExternalId,
                                        )}
                                    />
                                </div>
                            </RestrictedComponent>
                        </ButtonDropdown>
                    }
                />
                <FilterLayoutBody>
                    <Table>
                        <TableHeader>
                            <EmptyTableHeaderColumn
                                width={ColumnWidth._24px}
                                delimiterBottom={true}
                            />
                            <TableHeaderColumn
                                name={txt.InternalId()}
                                direction={SortDirection.Ascending}
                                selected={true}
                                onClick={null}
                                width={ColumnWidth._72px}
                                delimiterBottom={true}
                            />
                            <Conditional c={showOperatorExternalId}>
                                <TableHeaderColumn
                                    name={txt.OperatorExternalId()}
                                    onClick={null}
                                    width={ColumnWidth._72px}
                                    delimiterBottom={true}
                                />
                            </Conditional>
                            <TableHeaderColumn
                                name={txt.Name()}
                                onClick={null}
                                width={ColumnWidth.variable}
                                delimiterBottom={true}
                            />
                            <Conditional c={showPaymentType}>
                                <TableHeaderColumn
                                    name={txt.PaymentType()}
                                    onClick={null}
                                    width={ColumnWidth._136px}
                                    delimiterBottom={true}
                                />
                            </Conditional>
                            <Conditional c={showCashboxType}>
                                <TableHeaderColumn
                                    name={txt.CashboxType()}
                                    onClick={null}
                                    width={ColumnWidth._136px}
                                    delimiterBottom={true}
                                />
                            </Conditional>
                            <TableHeaderColumn
                                name={txt.LastCashBoxLevel()}
                                onClick={null}
                                width={ColumnWidth._136px}
                                delimiterBottom={true}
                            />
                            <TableHeaderColumn
                                name={txt.LastCollection()}
                                onClick={null}
                                width={ColumnWidth._136px}
                                delimiterBottom={true}
                            />
                            <TableHeaderColumn
                                name={txt.LastVoltage()}
                                onClick={null}
                                width={ColumnWidth._108px}
                                delimiterBottom={true}
                            />
                            <TableHeaderColumn
                                name={txt.FirmwareStatus()}
                                onClick={null}
                                width={ColumnWidth._136px}
                                delimiterBottom={true}
                            />
                            <Conditional c={showSyncState}>
                                <TableHeaderColumn
                                    name={txt.SyncStatus()}
                                    onClick={null}
                                    width={ColumnWidth._136px}
                                    delimiterBottom={true}
                                />
                            </Conditional>
                            <EmptyTableHeaderColumn
                                width={ColumnWidth._24px}
                                delimiterBottom={true}
                            />
                        </TableHeader>
                        <TableBody>
                            {tomsToDisplay.map((t: Tom.ParsedTom) => {
                                return (
                                    <TomItem
                                        tom={t}
                                        language={language}
                                        allState={store}
                                        showOperatorExternalId={
                                            showOperatorExternalId
                                        }
                                        showPaymentType={showPaymentType}
                                        showCashboxType={showCashboxType}
                                        showSyncState={showSyncState}
                                        key={t.tomId}
                                    />
                                );
                            })}
                        </TableBody>
                    </Table>
                </FilterLayoutBody>
            </FilterLayout>
        </Body>
    );
}
